// Client/src/pages/digi/Invoice/Components/TemplateReviewTab.jsx
//
// Staff side of vendor invoice templates: the queue of templates vendors sent
// for approval, and the review dialog that shows the submitted version next to
// the one currently in use. Approving lets that vendor's invoices for this
// company be generated from the template (no uploaded file needed).

import { useState, useEffect, useCallback, useRef } from "react";
import { Paper } from "@mui/material";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  listTemplateReviews,
  getTemplateReview,
  renderTemplateReview,
  decideTemplateReview,
  APPROVAL_LABEL,
} from "./templateApi";

const ACCENT = "#4F46E5";
// Width the canvas render needs: a Letter sheet (215.9 mm ≈ 816 px) plus its page gutter.
const SHEET_PX = 880;

const FILTERS = [
  { value: "pending", label: "Awaiting review", icon: "clock", color: "#D97706" },
  { value: "approved", label: "Approved", icon: "check-circle", color: "#059669" },
  { value: "changes_requested", label: "Changes requested", icon: "alert-circle", color: "#DC2626" },
  { value: "all", label: "All", icon: "list", color: "#475569" },
];

const TONE = {
  warning: { bg: "#FFFBEB", fg: "#92400E", border: "#FDE68A" },
  success: { bg: "#ECFDF5", fg: "#065F46", border: "#A7F3D0" },
  danger: { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
};

const StatusPill = ({ status }) => {
  const l = APPROVAL_LABEL[status] || { text: status, tone: "warning" };
  const t = TONE[l.tone] || TONE.warning;
  return (
    <span style={{
      display: "inline-flex", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700,
      background: t.bg, color: t.fg, border: `1px solid ${t.border}`,
    }}>{l.text}</span>
  );
};

/**
 * A rendered invoice page, scaled to fit its column. The HTML is the server's
 * render of the vendor's design; the iframe is fully sandboxed (no scripts, no
 * same-origin access), so it can only display.
 */
const ScaledFrame = ({ html, title, loading }) => {
  const wrap = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = width ? Math.min(1, width / SHEET_PX) : 0.6;
  const frameHeight = 1150; // one Letter page plus gutter at 1:1
  return (
    <div ref={wrap} style={{
      position: "relative", width: "100%", height: frameHeight * scale, overflow: "hidden",
      background: "#E9ECEF", borderRadius: 10, border: "1px solid #E2E8F0",
    }}>
      {html && (
        <iframe
          title={title}
          sandbox=""
          srcDoc={html}
          style={{
            width: SHEET_PX, height: frameHeight, border: 0, transform: `scale(${scale})`,
            transformOrigin: "0 0", background: "#E9ECEF",
          }}
        />
      )}
      {loading && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(255,255,255,0.6)",
        }}>
          <Spinner style={{ color: ACCENT }} />
        </div>
      )}
    </div>
  );
};

// ── Review dialog ────────────────────────────────────────────────────────

const ReviewDialog = ({ row, onClose, onDecided }) => {
  const [detail, setDetail] = useState(null);
  const [sample, setSample] = useState("demo");
  const [html, setHtml] = useState({ submitted: "", current: "" });
  const [rendering, setRendering] = useState(false);
  const [note, setNote] = useState("");
  const [deciding, setDeciding] = useState(null);
  const [noteError, setNoteError] = useState("");

  const templateId = row?.templateId;
  const companyId = row?.companyId;

  useEffect(() => {
    if (!row) return;
    setDetail(null);
    setSample("demo");
    setNote("");
    setNoteError("");
    getTemplateReview(templateId, companyId).then(setDetail).catch(() => setDetail(null));
  }, [row, templateId, companyId]);

  const version = detail?.version ?? row?.version;
  const approvedVersion = detail?.approvedVersion ?? row?.approvedVersion ?? 0;
  const compare = approvedVersion > 0 && approvedVersion !== version;

  useEffect(() => {
    if (!row || !version) return undefined;
    let cancelled = false;
    setRendering(true);
    Promise.all([
      renderTemplateReview(templateId, companyId, version, sample).catch(() => null),
      compare ? renderTemplateReview(templateId, companyId, approvedVersion, sample).catch(() => null) : Promise.resolve(null),
    ]).then(([a, b]) => {
      if (cancelled) return;
      setHtml({ submitted: a?.html || "", current: b?.html || "" });
      if (!a?.html) toast.error("Could not show the template preview");
    }).finally(() => { if (!cancelled) setRendering(false); });
    return () => { cancelled = true; };
  }, [row, templateId, companyId, version, approvedVersion, compare, sample]);

  const pending = (detail?.status ?? row?.status) === "pending";
  const issues = detail?.issues || [];
  const errors = issues.filter((i) => i.level === "error");

  const decide = async (decision) => {
    if (decision === "changes_requested" && !note.trim()) {
      setNoteError("Tell the vendor what to change");
      return;
    }
    setDeciding(decision);
    try {
      await decideTemplateReview(templateId, { companyId, version, decision, note: note.trim() });
      toast.success(decision === "approve" ? "Template approved" : "Changes requested — the vendor has been emailed");
      onDecided();
    } catch {
      // toast shown by the api helper
    } finally {
      setDeciding(null);
    }
  };

  return (
    <Modal isOpen={!!row} toggle={() => !deciding && onClose()} size="xl" centered scrollable style={{ maxWidth: 1280 }}>
      <ModalHeader toggle={() => !deciding && onClose()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span>{row?.vendor?.name || "Vendor"} · {row?.name}</span>
          <span className="iv-chip iv-chip--indigo">v{version}</span>
          {row && <StatusPill status={detail?.status ?? row.status} />}
        </div>
      </ModalHeader>
      <ModalBody>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ fontSize: "0.8rem", color: "#475569" }}>
            {compare
              ? <>Submitted <strong>v{version}</strong> next to <strong>v{approvedVersion}</strong>, the version invoices use today.</>
              : approvedVersion === version
                ? <>This version is the one in use.</>
                : <>First version of this template — once approved, this vendor&apos;s invoices are generated from it.</>}
          </div>
          <div role="group" aria-label="Preview data" style={{ display: "flex", gap: 6 }}>
            {[{ v: "demo", l: "Sample data" }, { v: "latest", l: "Their latest invoice" }].map((o) => (
              <button key={o.v} type="button" aria-pressed={sample === o.v} onClick={() => setSample(o.v)}
                style={{
                  borderRadius: 8, padding: "6px 12px", fontSize: "0.76rem", fontWeight: 700, cursor: "pointer",
                  background: sample === o.v ? "#EEF2FF" : "#F8FAFC",
                  border: `1px solid ${sample === o.v ? "#C7D2FE" : "#E2E8F0"}`,
                  color: sample === o.v ? ACCENT : "#64748B",
                }}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: compare ? "1fr 1fr" : "minmax(0, 820px)", gap: 16, justifyContent: "center" }}>
          <div>
            <div style={{ fontSize: "0.75rem", fontWeight: 800, color: "#0F172A", marginBottom: 6 }}>
              {compare ? `Submitted · v${version}` : `v${version}`}
            </div>
            <ScaledFrame html={html.submitted} loading={rendering} title={`Template version ${version}`} />
          </div>
          {compare && (
            <div>
              <div style={{ fontSize: "0.75rem", fontWeight: 800, color: "#64748B", marginBottom: 6 }}>
                In use today · v{approvedVersion}
              </div>
              <ScaledFrame html={html.current} loading={rendering} title={`Template version ${approvedVersion}`} />
            </div>
          )}
        </div>

        {issues.length > 0 && (
          <div style={{ marginTop: 14, border: "1px solid #FDE68A", background: "#FFFBEB", borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ fontWeight: 800, fontSize: "0.78rem", color: "#92400E", marginBottom: 4 }}>
              Checks ({issues.length})
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: "0.78rem", color: "#78350F" }}>
              {issues.map((i, n) => (
                <li key={n}><strong>{i.level === "error" ? "Problem" : "Note"}:</strong> {i.message}</li>
              ))}
            </ul>
          </div>
        )}

        {pending && (
          <div style={{ marginTop: 14 }}>
            <Label for="tpl-review-note" style={{ fontWeight: 700, fontSize: "0.8rem" }}>
              Note to the vendor <span style={{ fontWeight: 500, color: "#94A3B8" }}>(required to request changes)</span>
            </Label>
            <Input id="tpl-review-note" type="textarea" rows={2} value={note}
              onChange={(e) => { setNote(e.target.value); setNoteError(""); }}
              placeholder="e.g. Please show your GST number under the logo." invalid={!!noteError} />
            {noteError && <div role="alert" style={{ color: "#DC2626", fontSize: "0.74rem", marginTop: 4 }}>{noteError}</div>}
          </div>
        )}
        {!pending && detail?.note && (
          <div style={{ marginTop: 14, fontSize: "0.8rem", color: "#475569" }}>
            <strong>Note sent:</strong> <span style={{ whiteSpace: "pre-wrap" }}>{detail.note}</span>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" onClick={onClose} disabled={!!deciding}
          style={{ borderRadius: 8, padding: "8px 14px", fontSize: "0.8rem", fontWeight: 700, background: "#fff", border: "1px solid #E2E8F0", color: "#334155" }}>
          Close
        </button>
        {pending && (
          <>
            <button type="button" onClick={() => decide("changes_requested")} disabled={!!deciding}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, padding: "8px 14px",
                fontSize: "0.8rem", fontWeight: 700, background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C",
              }}>
              {deciding === "changes_requested" ? <Spinner size="sm" style={{ width: 13, height: 13 }} /> : <FeatherIcon icon="corner-up-left" size={14} />}
              Request changes
            </button>
            <button type="button" onClick={() => decide("approve")} disabled={!!deciding || errors.length > 0}
              title={errors.length ? "This version has problems the vendor must fix first" : ""}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, padding: "8px 16px",
                fontSize: "0.8rem", fontWeight: 700, background: "#059669", border: "none", color: "#fff",
                opacity: errors.length ? 0.5 : 1,
              }}>
              {deciding === "approve" ? <Spinner size="sm" style={{ width: 13, height: 13 }} /> : <FeatherIcon icon="check" size={14} />}
              Approve
            </button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

// ── Tab ──────────────────────────────────────────────────────────────────

const TemplateReviewTab = ({ onCountChange }) => {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listTemplateReviews({ status });
      const arr = Array.isArray(list) ? list : [];
      setRows(arr);
      if (status === "pending") onCountChange?.(arr.length);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, onCountChange]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Paper elevation={0} className="iv-wrap iv-paper">
        <div className="iv-header">
          <div className="iv-header__left">
            <div className="iv-header__icon-wrap">
              <FeatherIcon icon="layout" size={18} color="#A5B4FC" />
            </div>
            <div className="iv-header__meta">
              <div className="iv-header__label">Invoices</div>
              <div className="iv-header__title">Vendor Templates</div>
            </div>
          </div>
          <div role="group" aria-label="Filter by status" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {FILTERS.map((f) => (
              <button key={f.value} type="button" aria-pressed={status === f.value} onClick={() => setStatus(f.value)}
                style={{
                  display: "flex", alignItems: "center", gap: 6, borderRadius: 8, padding: "6px 12px",
                  fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
                  background: status === f.value ? "#EEF2FF" : "#F8FAFC",
                  border: `1px solid ${status === f.value ? "#C7D2FE" : "#E2E8F0"}`,
                  color: status === f.value ? ACCENT : "#64748B",
                }}>
                <FeatherIcon icon={f.icon} size={13} color={f.color} /> {f.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="iv-tbl" style={{ minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ minWidth: 180, textAlign: "left", paddingLeft: 12 }}>Vendor</th>
                <th style={{ minWidth: 180, textAlign: "left", paddingLeft: 10 }}>Template</th>
                <th style={{ width: 90 }}>Version</th>
                <th style={{ minWidth: 120 }}>Submitted</th>
                <th style={{ minWidth: 140 }}>Status</th>
                <th className="iv-actions-th" style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: ACCENT }} /></td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="iv-empty-cell">
                    <div className="iv-empty" style={{ minHeight: "25vh" }}>
                      <div className="iv-empty__icon-wrap"><FeatherIcon icon="layout" size={24} color={ACCENT} /></div>
                      <div className="iv-empty__title">Nothing here</div>
                      <div className="iv-empty__sub">
                        {status === "pending" ? "No vendor templates are waiting for review." : "No templates in this list."}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={`${r.templateId}-${r.companyId}`} className="iv-row" style={{ cursor: "pointer" }} onClick={() => setOpen(r)}>
                  <td style={{ textAlign: "left", paddingLeft: 12 }}>
                    <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{r.vendor?.name || "—"}</div>
                    <div style={{ fontSize: "0.72rem", color: "#64748B" }}>{r.vendor?.email || ""}</div>
                  </td>
                  <td style={{ textAlign: "left", paddingLeft: 10, fontSize: "0.82rem", color: "#0F172A" }}>{r.name}</td>
                  <td style={{ textAlign: "center", fontSize: "0.8rem" }}>
                    v{r.version}
                    {r.approvedVersion > 0 && r.approvedVersion !== r.version && (
                      <div style={{ fontSize: "0.68rem", color: "#94A3B8" }}>v{r.approvedVersion} in use</div>
                    )}
                  </td>
                  <td style={{ textAlign: "center", fontSize: "0.78rem", color: "#475569" }}>
                    {r.submittedAt ? moment(r.submittedAt).format("MMM D, YYYY") : "—"}
                  </td>
                  <td style={{ textAlign: "center" }}><StatusPill status={r.status} /></td>
                  <td className="iv-actions-td" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="iv-ebtn" style={{ width: "auto", padding: "0 10px", gap: 5, display: "inline-flex", alignItems: "center" }}
                      onClick={() => setOpen(r)}>
                      <FeatherIcon icon="eye" size={13} color="#0284C7" />
                      <span style={{ fontSize: "0.74rem", fontWeight: 700, color: "#0284C7" }}>{r.status === "pending" ? "Review" : "View"}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      <ReviewDialog row={open} onClose={() => setOpen(null)} onDecided={() => { setOpen(null); load(); }} />
    </>
  );
};

export { TemplateReviewTab };
